import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  Paper,
  Typography,
  Box,
  Button,
  LinearProgress,
  Chip,
  Alert,
  Stack,
  alpha,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ReplayIcon from "@mui/icons-material/Replay";
import {
  getJob,
  downloadResult,
  processFile,
  finalizeJob,
  retryFile,
  type JobWithFiles,
} from "../services/api";
import { tokens } from "../theme";

const CONCURRENCY = 5;

export default function JobStatusPage() {
  const { jobId } = useParams<{ jobId: string }>();
  const [job, setJob] = useState<JobWithFiles | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [retrying, setRetrying] = useState<Set<string>>(new Set());

  const handleRetry = async (fileId: string) => {
    if (!jobId) return;
    setRetrying((prev) => new Set(prev).add(fileId));
    setError(null);
    try {
      await retryFile(jobId, fileId);
      const updated = await getJob(jobId);
      setJob(updated);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setRetrying((prev) => {
        const next = new Set(prev);
        next.delete(fileId);
        return next;
      });
    }
  };

  // 失敗ファイル全部を並列 3 で再試行
  const handleRetryAll = async () => {
    if (!jobId || !job) return;
    const failedFiles = job.files.filter((f) => f.status === "failed");
    if (failedFiles.length === 0) return;

    setError(null);
    setRetrying(new Set(failedFiles.map((f) => f.id)));

    const queue = [...failedFiles];
    const worker = async () => {
      while (queue.length > 0) {
        const file = queue.shift();
        if (!file) break;
        try {
          await retryFile(jobId, file.id);
        } catch {
          /* per-file failure */
        }
        // 個別 chip を done に
        setRetrying((prev) => {
          const next = new Set(prev);
          next.delete(file.id);
          return next;
        });
        try {
          const updated = await getJob(jobId);
          setJob(updated);
        } catch {
          /* ignore */
        }
      }
    };
    await Promise.all(
      Array.from({ length: Math.min(3, failedFiles.length) }, worker),
    );
  };

  // クライアント駆動: pending ファイルを並列に process-file API で処理
  // (Cloudflare Worker の waitUntil 30s 制限を回避)
  useEffect(() => {
    if (!jobId) return;
    let cancelled = false;

    (async () => {
      try {
        // 初期ロード
        const initial = await getJob(jobId);
        if (cancelled) return;
        setJob(initial);

        // 未完了ファイルを抽出 (job.status は信用しない: 過去の finalize で誤って completed
        // になっているケースを救う)
        const targetLang = initial.target_lang;
        const needsProcessing = (status: string): boolean => {
          if (status === "translated" || status === "failed") return false;
          if (status === "ocr_done" && (!targetLang || targetLang === "none"))
            return false;
          return true;
        };
        const pending = initial.files.filter((f) => needsProcessing(f.status));

        // 全ファイル終端状態
        if (pending.length === 0) {
          // 結果未生成なら finalize だけ呼ぶ
          if (!initial.result_r2_key) {
            await finalizeJob(jobId);
            const final = await getJob(jobId);
            if (!cancelled) setJob(final);
          }
          return;
        }

        // 並列ワーカーで pending をさばく
        const queue = [...pending];
        const refresh = async () => {
          try {
            const updated = await getJob(jobId);
            if (!cancelled) setJob(updated);
          } catch {
            /* ignore transient refresh errors */
          }
        };

        const worker = async () => {
          while (queue.length > 0 && !cancelled) {
            const file = queue.shift();
            if (!file) break;
            try {
              await processFile(jobId, file.id);
            } catch {
              /* per-file failure は server 側で記録済み */
            }
            await refresh();
          }
        };

        await Promise.all(
          Array.from({ length: Math.min(CONCURRENCY, pending.length) }, worker),
        );

        if (cancelled) return;

        // 最終化
        await finalizeJob(jobId);
        const final = await getJob(jobId);
        if (!cancelled) setJob(final);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : String(err));
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [jobId]);

  if (error) {
    return (
      <Alert
        severity="error"
        sx={{
          bgcolor: alpha("#EF4444", 0.08),
          border: `1px solid ${alpha("#EF4444", 0.2)}`,
        }}
      >
        {error}
      </Alert>
    );
  }

  if (!job) {
    return (
      <Box>
        <LinearProgress sx={{ mb: 2 }} />
        <Typography variant="caption" color="text.secondary">
          読み込み中…
        </Typography>
      </Box>
    );
  }

  const progressPercent =
    job.total_files > 0 ? (job.completed_files / job.total_files) * 100 : 0;
  const isWorking = job.status === "processing" || job.status === "created";

  return (
    <Stack spacing={{ xs: 3, md: 4 }}>
      {/* === Hero === */}
      <Stack direction="row" alignItems="center" spacing={{ xs: 1.5, md: 2 }}>
        <StatusIndicator status={job.status} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block" }}
          >
            ジョブ {jobId?.slice(0, 8)}
          </Typography>
          <Typography variant="h5">{jobStatusLabel(job.status)}</Typography>
        </Box>
        <Button
          component={Link}
          to="/"
          variant="outlined"
          size="small"
          startIcon={<HomeOutlinedIcon sx={{ fontSize: 16 }} />}
          sx={{
            minWidth: { xs: 40, sm: "auto" },
            px: { xs: 1, sm: 1.5 },
            "& .MuiButton-startIcon": { mr: { xs: 0, sm: 1 } },
          }}
        >
          <Box sx={{ display: { xs: "none", sm: "inline" } }}>ホーム</Box>
        </Button>
      </Stack>

      {/* === Progress === */}
      <Paper sx={{ p: { xs: 2, md: 3 } }}>
        <Stack direction="row" justifyContent="space-between" sx={{ mb: 1.5 }}>
          <Typography variant="caption" color="text.secondary">
            進捗
          </Typography>
          <Typography variant="caption" sx={{ color: "text.primary" }}>
            {job.completed_files} / {job.total_files}
          </Typography>
        </Stack>
        <LinearProgress
          variant="determinate"
          value={progressPercent}
          sx={{
            height: 6,
            borderRadius: 3,
            "& .MuiLinearProgress-bar": {
              borderRadius: 3,
              background:
                job.status === "completed"
                  ? "#22C55E"
                  : `linear-gradient(90deg, ${tokens.accent}, ${alpha(tokens.accent, 0.7)})`,
            },
          }}
        />

        {job.status === "failed" && job.error_message && (
          <Alert
            severity="error"
            sx={{
              mt: 2,
              bgcolor: alpha("#EF4444", 0.08),
              border: `1px solid ${alpha("#EF4444", 0.2)}`,
              whiteSpace: "pre-wrap",
            }}
          >
            {job.error_message}
          </Alert>
        )}

        {job.status === "completed" && job.error_message && (
          <Alert
            severity="warning"
            sx={{
              mt: 2,
              bgcolor: alpha("#F59E0B", 0.08),
              border: `1px solid ${alpha("#F59E0B", 0.2)}`,
              whiteSpace: "pre-wrap",
            }}
          >
            {job.error_message}
          </Alert>
        )}

        {/* 失敗ファイルがあるなら一括再試行 */}
        {(() => {
          const failedCount = job.files.filter((f) => f.status === "failed").length;
          if (failedCount === 0) return null;
          const anyRetrying = retrying.size > 0;
          return (
            <Button
              variant="outlined"
              onClick={handleRetryAll}
              disabled={anyRetrying}
              startIcon={<ReplayIcon />}
              fullWidth
              sx={{
                mt: 2,
                borderColor: alpha("#F59E0B", 0.4),
                color: "#F59E0B",
                "&:hover": {
                  borderColor: "#F59E0B",
                  backgroundColor: alpha("#F59E0B", 0.05),
                },
              }}
            >
              {anyRetrying
                ? `再試行中... (${retrying.size}/${failedCount})`
                : `失敗した ${failedCount} 件を全て再試行`}
            </Button>
          );
        })()}

        {job.status === "completed" && (
          <Button
            variant="contained"
            size="large"
            onClick={async () => {
              try {
                await downloadResult(job.id);
              } catch (err) {
                setError(err instanceof Error ? err.message : String(err));
              }
            }}
            startIcon={<DownloadIcon />}
            fullWidth
            sx={{ mt: 3 }}
          >
            結果ファイルをダウンロード
          </Button>
        )}
      </Paper>

      {/* === Files === */}
      <Paper sx={{ p: { xs: 2, md: 3 } }}>
        <Typography
          variant="caption"
          sx={{
            display: "block",
            mb: 2,
            color: "text.secondary",
            textTransform: "uppercase",
            letterSpacing: "0.05em",
            fontSize: 11,
            fontWeight: 500,
            fontFamily: "inherit !important",
          }}
        >
          ファイル ({job.files.length})
        </Typography>
        <Stack spacing={1}>
          {job.files.map((file) => (
            <Box
              key={file.id}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                px: 2,
                py: 1.25,
                borderRadius: 1.5,
                border: `1px solid ${tokens.border}`,
                backgroundColor: alpha("#fff", 0.02),
                position: "relative",
                overflow: "hidden",
              }}
            >
              {(file.status === "ocr_processing" ||
                file.status === "translating") && (
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    background: `linear-gradient(90deg, transparent 0%, ${alpha(tokens.accent, 0.08)} 50%, transparent 100%)`,
                    animation: "shimmer 1.6s linear infinite",
                    "@keyframes shimmer": {
                      "0%": { transform: "translateX(-100%)" },
                      "100%": { transform: "translateX(100%)" },
                    },
                  }}
                />
              )}
              <Box sx={{ flex: 1, minWidth: 0, position: "relative", zIndex: 1 }}>
                <Typography
                  variant="body2"
                  sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {file.file_name}
                </Typography>
                {file.status === "failed" && file.error_message && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: "error.main",
                      fontFamily: "inherit !important",
                      display: "block",
                      mt: 0.25,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {file.error_message}
                  </Typography>
                )}
              </Box>
              <Chip
                label={
                  retrying.has(file.id) ? "再試行中..." : fileStatusLabel(file.status)
                }
                size="small"
                color={
                  retrying.has(file.id)
                    ? "warning"
                    : fileStatusColor(file.status)
                }
                variant={file.status === "translated" ? "filled" : "outlined"}
                sx={{ position: "relative", zIndex: 1, flexShrink: 0 }}
              />
              {file.status === "failed" && (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => handleRetry(file.id)}
                  disabled={retrying.has(file.id)}
                  startIcon={<ReplayIcon sx={{ fontSize: 14 }} />}
                  sx={{
                    position: "relative",
                    zIndex: 1,
                    flexShrink: 0,
                    fontSize: 12,
                    py: 0.5,
                    minWidth: { xs: 36, sm: 80 },
                    px: { xs: 1, sm: 1.5 },
                    "& .MuiButton-startIcon": { mr: { xs: 0, sm: 0.5 } },
                  }}
                >
                  <Box sx={{ display: { xs: "none", sm: "inline" } }}>再試行</Box>
                </Button>
              )}
            </Box>
          ))}
        </Stack>
      </Paper>

      {isWorking && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
            color: "text.secondary",
          }}
        >
          <AutoAwesomeIcon sx={{ fontSize: 14, color: tokens.accent }} />
          <Typography variant="caption">
            Gemini が画像を読み取っています
          </Typography>
        </Box>
      )}
    </Stack>
  );
}

function StatusIndicator({ status }: { status: string }) {
  if (status === "completed") {
    return (
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: alpha("#22C55E", 0.12),
          border: `1px solid ${alpha("#22C55E", 0.3)}`,
        }}
      >
        <CheckCircleIcon sx={{ color: "#22C55E", fontSize: 20 }} />
      </Box>
    );
  }
  if (status === "failed") {
    return (
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: alpha("#EF4444", 0.12),
          border: `1px solid ${alpha("#EF4444", 0.3)}`,
        }}
      >
        <ErrorOutlineIcon sx={{ color: "#EF4444", fontSize: 20 }} />
      </Box>
    );
  }
  return (
    <Box
      sx={{
        width: 40,
        height: 40,
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: alpha(tokens.accent, 0.12),
        border: `1px solid ${alpha(tokens.accent, 0.3)}`,
        position: "relative",
        "&::before": {
          content: '""',
          position: "absolute",
          inset: -4,
          border: `1px solid ${alpha(tokens.accent, 0.3)}`,
          borderRadius: "50%",
          animation: "pulse 2s ease-in-out infinite",
          "@keyframes pulse": {
            "0%, 100%": { opacity: 1, transform: "scale(1)" },
            "50%": { opacity: 0, transform: "scale(1.2)" },
          },
        },
      }}
    >
      <AutoAwesomeIcon sx={{ color: tokens.accent, fontSize: 20 }} />
    </Box>
  );
}

function jobStatusLabel(s: string): string {
  switch (s) {
    case "created":
      return "開始中";
    case "processing":
      return "処理中";
    case "completed":
      return "完了";
    case "failed":
      return "失敗";
    default:
      return s;
  }
}

function fileStatusLabel(s: string): string {
  switch (s) {
    case "pending":
      return "待機";
    case "uploaded":
      return "準備完了";
    case "ocr_processing":
      return "OCR 中";
    case "ocr_done":
      return "OCR 完了";
    case "translating":
      return "翻訳中";
    case "translated":
      return "翻訳完了";
    case "failed":
      return "失敗";
    default:
      return s;
  }
}

function fileStatusColor(
  s: string,
): "default" | "primary" | "success" | "error" | "warning" {
  switch (s) {
    case "translated":
      return "success";
    case "ocr_done":
      return "primary";
    case "ocr_processing":
    case "translating":
      return "warning";
    case "failed":
      return "error";
    default:
      return "default";
  }
}
